import { useMutation } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import useUser from '../global/useUser';
import { uploadFiles } from '../../services/global';
import {
  EmployeeWaiverQuestions,
  getEmployeeWaiverQuestionsSchema,
  GetWaiverRequestResponse,
} from '../../types/waivers';
import EmployeeWaiverPDF from '../../components/waivers/EmployeeWaiverPDF';
import { getStorageUrl } from '../../helpers/global';
import { mergePDFs, renderPDFSafely } from '../../utils/pdf';
import { submitEmployeeWaiver } from '../../services/waivers';
import { postMessageToReactNative } from '../../utils/global/helper';

interface SubmitWaiverOptions {
  waiverRequest: GetWaiverRequestResponse['waiverRequest'] | undefined;
  waiver: GetWaiverRequestResponse['waiver'] | undefined;
  onSuccess?: () => void | Promise<void>;
  onError?: (
    error: Error,
    variables: EmployeeWaiverQuestions,
  ) => void | Promise<void>;
}

function useSubmitEmployeeWaiver({
  waiverRequest,
  waiver,
  onSuccess,
  onError,
}: SubmitWaiverOptions) {
  const { user } = useUser();

  const submitEmployeeWaiverMutation = useMutation({
    mutationFn: async (payload: EmployeeWaiverQuestions) => {
      try {
        if (
          !waiverRequest?.id ||
          !waiverRequest.signedFileLink ||
          !waiver ||
          !waiver.employeeSettings?.enabled ||
          !waiver.employeeSettings.options.length ||
          !waiver.business ||
          !user
        ) {
          throw new Error('Invalid waiver');
        }

        const waiverFormSchema = getEmployeeWaiverQuestionsSchema(
          waiver.employeeSettings.options ?? [],
        );

        const data = (await waiverFormSchema.validate(
          payload,
        )) as EmployeeWaiverQuestions;

        const originalWaiverPdfUrl = await getStorageUrl(
          waiverRequest.signedFileLink,
        );
        if (!originalWaiverPdfUrl)
          throw new Error('Failed to fetch original waiver PDF');

        const originalWaiverPdfBuffer = await fetch(originalWaiverPdfUrl).then(
          res => res.arrayBuffer(),
        );
        if (!originalWaiverPdfBuffer)
          throw new Error('Failed to fetch original waiver PDF');

        const pdfBuffer = await renderPDFSafely(
          () => (
            <EmployeeWaiverPDF waiverForm={data} waiver={waiver} user={user} />
          ),
          { waiverRequestId: waiverRequest.id },
        );

        const mergedPdfBuffer = await mergePDFs([
          originalWaiverPdfBuffer,
          pdfBuffer,
        ]);
        const pdfLocalUrl = URL.createObjectURL(
          new Blob([mergedPdfBuffer as BlobPart], { type: 'application/pdf' }),
        );

        const tempFileName = `${waiver.business.businessId}-${waiverRequest.appointmentId}`;
        const basePath = `tempImages/${tempFileName}`;

        const uploads = [
          {
            id: 'waiver',
            path: `${basePath}-signed-waiver-artist`,
            url: pdfLocalUrl,
          },
        ];

        const [pdfUrl] = await uploadFiles(uploads, {
          onSuccess: img => {
            const isObjectUrl = new URL(img.url).protocol === 'blob:';
            if (isObjectUrl) URL.revokeObjectURL(img.url);
          },
        });

        // replace signature and image fields with booleans before storing
        const answers = { ...data } as Record<string, string | boolean>;
        waiver.employeeSettings.options.forEach(option => {
          if (option.type === 'SIGNATURE') {
            const value = answers[option.id] as string;
            answers[option.id] = typeof value === 'string' && !!value.length;
          } else if (option.type === 'SINGLE_IMAGE') {
            const value = answers[option.id] as string;
            answers[option.id] = typeof value === 'string' && !!value.length;
          } else if (option.type === 'MULTIPLE_IMAGE') {
            const value = (data as Record<string, unknown>)[option.id] as
              | string[]
              | undefined;
            answers[option.id] = Array.isArray(value) && value.length > 0;
          }
        });

        const request = await submitEmployeeWaiver({
          answers,
          waiverRequestId: waiverRequest?.id,
          signedFileLink: pdfUrl,
        });

        postMessageToReactNative('waiver-submitted');

        return request;
      } catch (err) {
        // log to sentry with extra info
        Sentry.withScope(scope => {
          scope.setTag('feature', 'employee_waiver');
          scope.setTag('mutation', 'submitEmployeeWaiver');
          scope.setContext('waiver', {
            waiverRequestId: waiverRequest?.id,
            businessId: waiver?.business?.businessId,
          });
          Sentry.captureException(err);
        });
        throw err; // still propagate to react-query
      }
    },
    onSuccess,
    // Errors are already captured (with tags) in the mutationFn catch.
    onError,
  });

  return {
    submitWaiverMutation: submitEmployeeWaiverMutation,
    variables: submitEmployeeWaiverMutation.variables,
    isPending: submitEmployeeWaiverMutation.isPending,
    isSuccess: submitEmployeeWaiverMutation.isSuccess,
    isError: submitEmployeeWaiverMutation.isError,
    error: submitEmployeeWaiverMutation.error,
  };
}

export default useSubmitEmployeeWaiver;
